/**
 * @module module/paramset-sk
 * @description <h2><code>paramset-sk</code></h2>
 *
 * The paramset-sk element displays a paramset and generates events as the
 * params and labels are clicked.
 *
 * @evt paramset-key-click - Generated when the key for a paramset is clicked.
 *     The name of the key will be sent in e.detail.key. The value of
 *     e.detail.ctrl is true if the control key was pressed when clicking.
 *
 *      {
 *        key: "arch",
 *        ctrl: false,
 *      }
 *
 * @evt paramset-key-value-click - Generated when one value for a paramset is
 *     clicked. The name of the key will be sent in e.detail.key, the value in
 *     e.detail.value. The value of e.detail.ctrl is true if the control key was
 *     pressed when clicking.
 *
 *      {
 *        key: "arch",
 *        value: "x86",
 *        ctrl: false,
 *      }
 *
 * @evt paramset-checkbox-click - Generated when the checkbox for a paramset value
 *     is clicked.
 *
 *      {
 *        key: "arch",
 *        value: "x86",
 *        selected: true
 *      }
 *
 *  * @evt paramset-key-checkbox-click - Generated when the checkbox for a key in the paramset
 *     is clicked.
 *
 *      {
 *        key: "arch",
 *        values: ["x86", "x64"],
 *        selected: true
 *      }
 *
 * @evt plus-click - Generated when the plus sign is clicked. The element must
 *     have the 'clickable_plus' attribute set. The details of the event
 *     contains both the key and the values for the row, for example:
 *
 *      {
 *        key: "arch",
 *        values" ["x86", "risc-v"],
 *      }
 *
 * @evt paramset-value-remove-click - Generated when one value for a paramset is
 *     removed. The name of the key will be sent in e.detail.key, the value in
 *     e.detail.value.
 *
 *      {
 *        key: "arch",
 *        value: "x86",
 *      }
 *
 * @attr {string} clickable - If true then keys and values look like they are
 *     clickable i.e. via color, text-decoration, and cursor. If clickable is
 *     false then this element won't generate the events listed below, and the
 *     keys and values are not styled to look clickable. Setting both clickable
 *     and clickable_values is unsupported.
 *
 * @attr {string} clickable_values - If true then only the values are clickable.
 *     Setting both clickable and clickable_values is unsupported.
 *
 * @attr {string} clickable_plus - If true then a plus sign is added to every
 * row in the right hand column, that when pressed emits the plus-click event
 * that contains the key and values for that row.
 *
 * @attr {string} removable_values - If true then the cancel icon is displayed
 * next to each value in the paramset to remove the values from the set
 *
 * @attr {string} checkbox_values - If true, then the values displayed will have
 * a checkbox to let the user select/unselect the specific value.
 *
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "ParamSetSk", {
    enumerable: true,
    get: function() {
        return ParamSetSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
require("../../../elements-sk/modules/icons/add-icon-sk");
require("../../../elements-sk/modules/icons/cancel-icon-sk");
require("../../../elements-sk/modules/checkbox-sk");
require("../../../elements-sk/modules/toast-sk");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let ParamSetSk = class ParamSetSk extends _lit.LitElement {
    createRenderRoot() {
        return this;
    }
    firstUpdated() {
        this._toast = this.querySelector('toast-sk');
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('paramsets')) {
            // Compute a rolled up set of all parameter keys across all paramsets.
            const allKeys = new Set();
            this.paramsets.forEach((p)=>{
                Object.keys(p).forEach((key)=>{
                    allKeys.add(key);
                });
            });
            this._sortedKeys = Array.from(allKeys).sort();
            this._unchecked = new Map();
        }
    }
    render() {
        return (0, _lit.html)`
      <table @click=${this._click} class=${this.computeClass()}>
        <tbody>
          <tr>
            <th></th>
            ${this.titlesTemplate()}
          </tr>
          ${this.rowsTemplate()}
        </tbody>
      </table>
      <toast-sk duration="2000">Copied</toast-sk>
    `;
    }
    titlesTemplate() {
        return this.normalizedTitles().map((t)=>(0, _lit.html)`<th>${t}</th>`);
    }
    rowsTemplate() {
        return this._sortedKeys.map((key)=>this.rowTemplate(key));
    }
    rowTemplate(key) {
        if (this.checkbox_values) {
            // If this row contains only one value, let's disable the key checkbox.
            let disabled = false;
            this.paramsets.forEach((ps)=>{
                const vals = ps[key];
                if (vals && vals.length <= 1) {
                    disabled = true;
                }
            });
            return (0, _lit.html)`<tr>
        <th data-key=${key}>
          <checkbox-sk
            id="selectAll-${key}"
            name=""
            @change=${(e)=>this.paramsetKeySelectAllHandler(e, key)}
            label=""
            checked
            ?disabled=${disabled}
            title="Select/Unselect this value from the graph.">
          </checkbox-sk>
          ${key}
        </th>
        ${this.paramsetValuesTemplate(key)}
      </tr>`;
        } else {
            return (0, _lit.html)`<tr>
        <th data-key=${key}>${key}</th>
        ${this.paramsetValuesTemplate(key)}
      </tr>`;
        }
    }
    paramsetValuesTemplate(key) {
        const ret = [];
        this.paramsets.forEach((p)=>ret.push((0, _lit.html)`<td>${this.paramsetValueTemplate(key, p[key] || [])}</td>`, this.optionalPlusSign(key, p), this.optionalCopyContent(key, p)));
        return ret;
    }
    optionalPlusSign(key, p) {
        if (!this.clickable_plus) {
            return (0, _lit.html)``;
        }
        return (0, _lit.html)` <td>
      <add-icon-sk data-key=${key} data-values=${JSON.stringify(p[key])}></add-icon-sk>
    </td>`;
    }
    optionalCopyContent(key, p) {
        if (!this.copy_content) {
            return (0, _lit.html)``;
        }
        return (0, _lit.html)` <td>
      <div class="icon-sk copy-content" @click=${()=>this.copyContent(`${key}=${p[key]}`)}>
        content_copy
      </div>
    </td>`;
    }
    paramsetValueTemplate(key, params) {
        // Figure out if we are down to just one checkbox being checked. If so we'll
        // want to disable that checkbox so that it can't be unchecked, otherwise
        // all the data will disappear from the display.
        let downToJustOneCheckedCheckboxForThisKey = false;
        // Count the number of unchecked values for this key.
        let numUnchecked = 0;
        const uncheckedSet = this._unchecked.get(key);
        if (uncheckedSet !== undefined) {
            numUnchecked = uncheckedSet.size;
        }
        if (params.length - numUnchecked <= 1) {
            downToJustOneCheckedCheckboxForThisKey = true;
        }
        return params.map((value)=>{
            if (this.checkbox_values) {
                let disabled = false;
                const currentCheckboxChecked = uncheckedSet === undefined || !uncheckedSet.has(value);
                if (downToJustOneCheckedCheckboxForThisKey && currentCheckboxChecked) {
                    disabled = true;
                }
                return (0, _lit.html)`
          <div class=${this.highlighted(key, value)} data-key=${key} data-value=${value}>
            <checkbox-sk
              id="checkbox-${key}-${value}"
              name=""
              @change=${(e)=>this.checkboxValueClickHandler(e, key, value)}
              label=""
              checked
              ?disabled=${disabled}
              title="Select/Unselect this value from the graph.">
            </checkbox-sk>
            ${value}
          </div>
        `;
            }
            return (0, _lit.html)`<div class=${this.highlighted(key, value)} data-key=${key} data-value=${value}>
        <span>${value}</span> ${this.cancelIconTemplate(key, value)}
      </div> `;
        });
    }
    cancelIconTemplate(key, value) {
        if (this.removable_values) {
            return (0, _lit.html)`<cancel-icon-sk
        id="${key}-${value}-remove"
        data-key=${key}
        data-value=${value}
        title="Negative"></cancel-icon-sk>`;
        }
        return (0, _lit.html)``;
    }
    computeClass() {
        if (this.clickable_values) {
            return 'clickable_values';
        }
        if (this.clickable) {
            return 'clickable';
        }
        return '';
    }
    highlighted(key, value) {
        return this.highlight[key] === value ? 'highlight' : '';
    }
    async copyContent(body) {
        var _this__toast;
        await navigator.clipboard.writeText(body);
        (_this__toast = this._toast) === null || _this__toast === void 0 ? void 0 : _this__toast.show();
    }
    fixUpDisabledStateOnRemainingCheckboxes(isChecked, key, value) {
        // Update the unchecked status and then re-render.
        const set = this._unchecked.get(key) || new Set();
        if (isChecked) {
            set.delete(value);
        } else {
            set.add(value);
        }
        this._unchecked.set(key, set);
    }
    paramsetKeySelectAllHandler(e, key) {
        const selectAll = e.target.checked;
        this.paramsets.forEach((p)=>{
            const vals = p[key];
            if (!vals) return;
            let keepCheckedIndex = -1;
            const valuesToUpdate = [];
            for(let i = 0; i < vals.length; i++){
                const checkbox_id = `checkbox-${key}-${vals[i]}`;
                const checkbox = this.querySelector(`#${CSS.escape(checkbox_id)}`);
                if (!checkbox) continue;
                // If we are unselecting, we want to keep the first checked item
                // from selected values around. This is because we would need at least
                // one trace in the graph.
                if (keepCheckedIndex === -1 && checkbox.checked && !selectAll) {
                    keepCheckedIndex = i;
                } else {
                    valuesToUpdate.push(vals[i]);
                    this.fixUpDisabledStateOnRemainingCheckboxes(selectAll, key, vals[i]);
                }
            }
            this.requestUpdate();
            const detail = {
                selected: selectAll,
                key: key,
                values: valuesToUpdate
            };
            // Send the event so that the graph can be updated to match the selection.
            this.dispatchEvent(new CustomEvent('paramset-key-checkbox-click', {
                detail,
                bubbles: true
            }));
        });
    }
    checkboxValueClickHandler(e, key, value) {
        const isChecked = e.target.checked;
        const detail = {
            selected: isChecked,
            key: key,
            value: value
        };
        this.dispatchEvent(new CustomEvent('paramset-checkbox-click', {
            detail,
            bubbles: true
        }));
        this.fixUpDisabledStateOnRemainingCheckboxes(isChecked, key, value);
        this.requestUpdate();
    }
    _click(e) {
        if (!this.clickable && !this.clickable_values && !this.clickable_plus && !this.removable_values) {
            return;
        }
        const t = e.target;
        // Check if we clicked "content_copy" which is a div
        if (t.classList.contains('copy-content')) {
            return;
        }
        const target = t.closest('[data-key]');
        if (!target) {
            return;
        }
        if (target.nodeName === 'TH') {
            if (!this.clickable) {
                return;
            }
            const detail = {
                key: target.dataset.key,
                ctrl: e.ctrlKey
            };
            this.dispatchEvent(new CustomEvent('paramset-key-click', {
                detail,
                bubbles: true
            }));
        } else if (target.nodeName === 'DIV') {
            // It could be a value click
            const detail = {
                key: target.dataset.key,
                value: target.dataset.value,
                ctrl: e.ctrlKey
            };
            this.dispatchEvent(new CustomEvent('paramset-key-value-click', {
                detail,
                bubbles: true
            }));
        } else {
            // Check if target is ADD-ICON-SK or CANCEL-ICON-SK
            // Since closest('[data-key]') might find them.
            if (target.nodeName === 'ADD-ICON-SK') {
                const detail = {
                    key: target.dataset.key,
                    values: JSON.parse(target.dataset.values)
                };
                this.dispatchEvent(new CustomEvent('plus-click', {
                    detail,
                    bubbles: true
                }));
            } else if (target.nodeName === 'CANCEL-ICON-SK') {
                this.removeParam(target.dataset.key, target.dataset.value);
            }
        }
    }
    // Returns the titles specified by the user, or an empty title for each paramset
    // if the number of specified titles and the number of paramsets don't match.
    normalizedTitles() {
        if (this.titles.length === this.paramsets.length) {
            return this.titles;
        }
        return new Array(this.paramsets.length).fill('');
    }
    removeParam(key, value) {
        const paramsets = [];
        this.paramsets.forEach((paramset)=>{
            const values = paramset[key];
            if (!values) {
                paramsets.push(paramset);
                return;
            }
            const valIndex = values.indexOf(value);
            if (valIndex > -1) {
                values.splice(valIndex, 1);
                if (values.length === 0) {
                    delete paramset[key];
                } else {
                    paramset[key] = values;
                }
            }
            paramsets.push(paramset);
        });
        // Set the current paramsets to the updated value
        this.paramsets = paramsets;
        const detail = {
            key: key,
            value: value
        };
        this.dispatchEvent(new CustomEvent('paramset-value-remove-click', {
            detail,
            bubbles: true
        }));
    }
    constructor(...args){
        super(...args);
        this.titles = [];
        this.paramsets = [];
        this.highlight = {};
        this.clickable = false;
        this.clickable_values = false;
        this.clickable_plus = false;
        this.checkbox_values = false;
        this.removable_values = false;
        this.copy_content = false;
        this._sortedKeys = [];
        this._unchecked = new Map();
        this._toast = null;
    }
};
_ts_decorate([
    (0, _decorators.property)({
        type: Array
    }),
    _ts_metadata("design:type", Array)
], ParamSetSk.prototype, "titles", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Array
    }),
    _ts_metadata("design:type", Array)
], ParamSetSk.prototype, "paramsets", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Object
    }),
    _ts_metadata("design:type", Object)
], ParamSetSk.prototype, "highlight", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], ParamSetSk.prototype, "clickable", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true,
        attribute: 'clickable_values'
    }),
    _ts_metadata("design:type", Boolean)
], ParamSetSk.prototype, "clickable_values", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true,
        attribute: 'clickable_plus'
    }),
    _ts_metadata("design:type", Boolean)
], ParamSetSk.prototype, "clickable_plus", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true,
        attribute: 'checkbox_values'
    }),
    _ts_metadata("design:type", Boolean)
], ParamSetSk.prototype, "checkbox_values", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true,
        attribute: 'removable_values'
    }),
    _ts_metadata("design:type", Boolean)
], ParamSetSk.prototype, "removable_values", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true,
        attribute: 'copy-content'
    }),
    _ts_metadata("design:type", Boolean)
], ParamSetSk.prototype, "copy_content", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Array)
], ParamSetSk.prototype, "_sortedKeys", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", typeof Map === "undefined" ? Object : Map)
], ParamSetSk.prototype, "_unchecked", void 0);
ParamSetSk = _ts_decorate([
    (0, _decorators.customElement)('paramset-sk')
], ParamSetSk);
